import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cx } from '../lib/cx'
import { MoneyError, formatKwacha, formatKwachaPlain, toNgwee } from '../lib/money'
import { Icon } from './Icon'
import './Field.css'

/** K500, K1,000 and K2,000 (PRD section 17). */
const DEFAULT_QUICK_PICKS_NGWEE: readonly number[] = [50_000, 100_000, 200_000]

export interface AmountInputProps {
  label: string
  /** The amount in integer ngwee, always whole kwacha; null when empty or unreadable. */
  valueNgwee: number | null
  onChange: (valueNgwee: number | null) => void
  /** Lowest amount allowed, in ngwee. Below it: "Minimum is K500", shown once she leaves the field. */
  minNgwee?: number
  /** Highest amount allowed, in ngwee. Above it the error shows at once. */
  maxNgwee?: number
  /** Replaces "Most you can enter is K…", e.g. "Most you can save is K12,000" at the savings cap. */
  maxMessage?: string
  /** One-tap amounts, in ngwee. Defaults to K500, K1,000 and K2,000; pass [] for none. */
  quickPicksNgwee?: readonly number[]
  hint?: string
  /** An error from the form or the server, shown instead of the field's own. */
  error?: string
  id?: string
  name?: string
  disabled?: boolean
  required?: boolean
  className?: string
}

type Problem = { key: 'invalid' | 'tooManyDecimals' | 'badGrouping' | 'tooLarge' } | { key: 'min' | 'max' }

function read(text: string): { ngwee: number | null; problem: Problem | null } {
  if (text.trim() === '') return { ngwee: null, problem: null }
  try {
    const ngwee = toNgwee(text)
    if (ngwee < 0 || ngwee % 100 !== 0) return { ngwee: null, problem: { key: 'invalid' } }
    return { ngwee, problem: null }
  } catch (error) {
    if (!(error instanceof MoneyError)) throw error
    const keys: Partial<Record<MoneyError['code'], 'tooManyDecimals' | 'badGrouping' | 'tooLarge'>> = {
      too_many_decimals: 'tooManyDecimals',
      bad_grouping: 'badGrouping',
      too_large: 'tooLarge',
    }
    return { ngwee: null, problem: { key: keys[error.code] ?? 'invalid' } }
  }
}

/**
 * The PRD AmountInput: whole kwacha, a numeric keypad, quick picks, and errors under
 * the field ("Minimum is K500") that screen readers announce. A 2 dp danger border
 * marks an error.
 */
export function AmountInput({
  label,
  valueNgwee,
  onChange,
  minNgwee,
  maxNgwee,
  maxMessage,
  quickPicksNgwee = DEFAULT_QUICK_PICKS_NGWEE,
  hint,
  error,
  id,
  name,
  disabled = false,
  required = false,
  className,
}: AmountInputProps) {
  const { t } = useTranslation()
  const generatedId = useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const picksId = `${inputId}-picks`
  const [text, setText] = useState(() => (valueNgwee === null ? '' : formatKwachaPlain(valueNgwee)))
  const [touched, setTouched] = useState(false)
  const [syncedValue, setSyncedValue] = useState(valueNgwee)

  // Follow changes made from outside (a reset, a pledge pre-fill) without fighting her typing.
  if (valueNgwee !== syncedValue) {
    setSyncedValue(valueNgwee)
    if (read(text).ngwee !== valueNgwee) setText(valueNgwee === null ? '' : formatKwachaPlain(valueNgwee))
  }

  // Too much shows at once; anything else waits until she leaves the field, so a
  // half-typed "1,0" on the way to "1,000" is not flagged.
  const parsed = read(text)
  let problem: Problem | null = touched ? parsed.problem : null
  if (!parsed.problem && parsed.ngwee !== null) {
    if (maxNgwee !== undefined && parsed.ngwee > maxNgwee) problem = { key: 'max' }
    else if (touched && minNgwee !== undefined && parsed.ngwee < minNgwee) problem = { key: 'min' }
  }

  let message: string | undefined = error
  if (!message && problem) {
    if (problem.key === 'min') message = t('kit.amountInput.min', { amount: formatKwacha(minNgwee ?? 0, { decimals: false }) })
    else if (problem.key === 'max') message = maxMessage ?? t('kit.amountInput.max', { amount: formatKwacha(maxNgwee ?? 0, { decimals: false }) })
    else message = t(`kit.amountInput.${problem.key}`)
  }

  const change = (next: string) => {
    setText(next)
    onChange(read(next).ngwee)
  }

  const describedBy = [hint ? hintId : '', message ? errorId : '', quickPicksNgwee.length ? picksId : ''].filter(Boolean).join(' ')

  return (
    <div className={cx('field', message && 'field--error', disabled && 'field--disabled', className)}>
      <label htmlFor={inputId} className="field__label">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="field__hint">
          {hint}
        </p>
      )}
      <div className="field__control">
        <span className="field__prefix" aria-hidden="true">
          K
        </span>
        <input
          id={inputId}
          name={name}
          className="field__input field__input--amount"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint="done"
          value={text}
          disabled={disabled}
          required={required}
          aria-invalid={message ? true : undefined}
          aria-describedby={describedBy || undefined}
          onChange={(event) => change(event.target.value)}
          onFocus={() => setText((current) => current.replaceAll(',', ''))}
          onBlur={() => {
            setTouched(true)
            const { ngwee } = read(text)
            if (ngwee !== null) setText(formatKwachaPlain(ngwee))
          }}
        />
      </div>
      {message && (
        <p id={errorId} className="field__error" role="alert">
          <Icon name="error" />
          <span>{message}</span>
        </p>
      )}
      {quickPicksNgwee.length > 0 && (
        <div className="quick-picks" role="group" aria-label={t('kit.amountInput.quickPicks')} id={picksId}>
          {quickPicksNgwee.map((pick) => (
            <button
              key={pick}
              type="button"
              className="quick-pick"
              aria-pressed={valueNgwee === pick}
              disabled={disabled || (maxNgwee !== undefined && pick > maxNgwee)}
              onClick={() => {
                setTouched(true)
                setText(formatKwachaPlain(pick))
                onChange(pick)
              }}
            >
              {formatKwacha(pick, { decimals: false })}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
